import { useEffect, useRef, useState } from 'react';
import type { HomeCategory } from '../types';

/**
 * Rail horizontal de categorias de Atividade (1ª linha de filtros) +
 * botão "Mais filtros" que abre um drawer com Público, Intensidade,
 * Tipo de reserva, Preço, Duração.
 *
 * Substitui a barra de 5 menus do FilterMenu antigo. Reduz fricção
 * mantendo poder de filtro.
 */
interface FilterRailProps {
  categories: HomeCategory[];          // todas (5 grupos)
  activeTags: string[];
  onToggle: (slug: string) => void;
  onClearAll: () => void;
}

const PRIMARY_GROUP = 'Atividade';     // grupo que vira o rail principal

export function FilterRail({ categories, activeTags, onToggle, onClearAll }: FilterRailProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const railRef = useRef<HTMLDivElement | null>(null);

  const primaryOptions = categories.filter((c) => c.group === PRIMARY_GROUP);
  const secondaryGroups = Array.from(
    new Set(categories.filter((c) => c.group !== PRIMARY_GROUP).map((c) => c.group)),
  );
  const activeSet = new Set(activeTags);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDrawerOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const secondaryActiveCount = activeTags.filter(
    (slug) => categories.find((c) => c.slug === slug)?.group !== PRIMARY_GROUP,
  ).length;

  return (
    <>
      <div className="filter-rail" ref={railRef}>
        <div className="filter-rail__scroll">
          {primaryOptions.map((opt) => {
            const active = activeSet.has(opt.slug);
            return (
              <button
                key={opt.slug}
                type="button"
                className={`chip${active ? ' chip--active' : ''}`}
                onClick={() => onToggle(opt.slug)}
                aria-pressed={active}
              >
                <span>{opt.label}</span>
                <span className="chip__count">{opt.count}</span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className={`btn btn--outline filter-rail__more${secondaryActiveCount > 0 ? ' filter-rail__more--active' : ''}`}
          onClick={() => setDrawerOpen(true)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          Mais filtros
          {secondaryActiveCount > 0 && <span className="filter-rail__badge">{secondaryActiveCount}</span>}
        </button>
      </div>

      {drawerOpen && (
        <div className="filter-drawer" role="dialog" aria-modal="true" aria-label="Mais filtros">
          <div className="filter-drawer__backdrop" onClick={() => setDrawerOpen(false)} />
          <div className="filter-drawer__sheet">
            <header className="filter-drawer__head">
              <h3>Mais filtros</h3>
              <button type="button" className="btn btn--ghost" onClick={() => setDrawerOpen(false)} aria-label="Fechar">×</button>
            </header>

            <div className="filter-drawer__body">
              {secondaryGroups.map((group) => {
                const opts = categories.filter((c) => c.group === group);
                return (
                  <section key={group} className="filter-drawer__group">
                    <h4>{group}</h4>
                    <div className="filter-drawer__chips">
                      {opts.map((opt) => {
                        const active = activeSet.has(opt.slug);
                        return (
                          <button
                            key={opt.slug}
                            type="button"
                            className={`chip${active ? ' chip--active' : ''}`}
                            onClick={() => onToggle(opt.slug)}
                            aria-pressed={active}
                          >
                            <span>{opt.label}</span>
                            <span className="chip__count">{opt.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>

            <footer className="filter-drawer__foot">
              <button type="button" className="btn btn--ghost" onClick={onClearAll}>Limpar tudo</button>
              <button type="button" className="btn btn--primary" onClick={() => setDrawerOpen(false)}>Aplicar</button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
